import { inject, Service } from '@angular/core';
import { ClientHttpService } from '@data-services/http/client-http.service';
import { NameMapEntry } from '@models/common';
import { DoorNote } from '@models/doors';
import { getFileNameFromContentDisposition } from '@utils';
import { DoorCount } from './door-count.model';
import { DoorNoteViewModel } from './door-note-view-model.model';
import { PrintDoorNoteRequest } from './print-door-note-request.model';
import { PrintDoorNoteResponse } from './print-door-note-response.model';

@Service()
export class DoorNoteDataService {
  private readonly http = inject(ClientHttpService);

  async getNoteCountsAsync(projectId: string): Promise<DoorCount[]> {
    return await this.http.getObjectAsync<DoorCount[]>(`project/${projectId}/doors/notes/count`);
  }

  async getNoteUserNamesAsync(projectId: string): Promise<NameMapEntry[]> {
    return await this.http.getObjectAsync<NameMapEntry[]>(`project/${projectId}/doors/notes/users`);
  }

  async getNotesAsync(projectId: string): Promise<DoorNoteViewModel[]> {
    return await this.http.getObjectAsync<DoorNoteViewModel[]>(`project/${projectId}/doors/notes`);
  }

  async getNotesForDoorAsync(projectId: string, doorId: string): Promise<DoorNote[]> {
    return await this.http.getObjectAsync<DoorNote[]>(`project/${projectId}/door/${doorId}/notes`);
  }

  async saveNotesAsync(note: DoorNoteViewModel): Promise<number> {
    return await this.http.postObjectAsync<DoorNoteViewModel, number>('project/doors/notes', note);
  }

  async deleteNoteAsync(projectId: string, doorId: string, noteId: string): Promise<boolean> {
    return await this.http.deleteAsync<boolean>(`project/${projectId}/doors/${doorId}/notes/${noteId}`);
  }

  async printNoteAsync(noteId: string, request: PrintDoorNoteRequest, fallbackFileName: string): Promise<PrintDoorNoteResponse> {
    const response = await this.http.postBlobAsync<PrintDoorNoteRequest>(`project/doors/notes/${noteId}/pdf`, request);
    const blob = response.body ?? new Blob();
    const fileName = getFileNameFromContentDisposition(response.headers.get('Content-Disposition'), fallbackFileName);
    return { blob, fileName };
  }
}
